import type { FC, ReactElement, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { CallBackProps, Placement, Step } from 'react-joyride';
import Joyride, { ACTIONS, EVENTS } from 'react-joyride';
import { useMediaQuery, useTheme } from '@mui/material';
import { useAtom } from 'jotai';

import type { StepDataEnhanced, StepUpstream } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';

import GuidedTourIcon from './components/guidedTourIcon';
import GuidedTourTooltip from './components/guidedTourTooltip';
import { guidedTourCompletedModules, guidedTourState } from './atom';
import { GUIDED_TOUR_BUTTON_ID, GuidedTourState } from './constants';

export interface GuidedTourValues {
  addSteps: (steps: Array<StepDataEnhanced | undefined>) => void;
  autoStartTour: () => void;
  disabled: boolean;
  enableGuidedTour: (autoStart?: boolean) => void;
  disableGuidedTour: () => void;
  startTour: () => void;
  startTourAt: (target: string | HTMLElement) => void;
  createStep: (
    id: number,
    stepData: StepUpstream | null,
  ) => ReactElement | null;
  removeStep: (id: number | string) => void;
}

export const GuidedTourContext = createContext<GuidedTourValues>({
  addSteps: () => {},
  autoStartTour: () => {},
  disabled: true,
  enableGuidedTour: () => {},
  disableGuidedTour: () => {},
  startTour: () => {},
  startTourAt: () => {},
  createStep: () => null,
  removeStep: () => null,
});

export interface GuidedTourContextProviderProps {
  stepAfterHook?: (nextTarget: string | HTMLElement) => void;
  children: ReactNode;
}

interface GuidedTourStep extends Step {
  appendToStart?: boolean;
  appendToEnd?: boolean;
  followUpLinks?: boolean;
  id: string | number;
}

export const GuidedTourContextProvider: FC<GuidedTourContextProviderProps> = ({
  stepAfterHook,
  children,
}) => {
  const { i18n } = useTranslation();
  const [state, setState] = useAtom(guidedTourState);
  const { moduleId } = useSection();
  const [completedModule, setCompletedModule] = useAtom(
    guidedTourCompletedModules,
  );
  const [index, setIndex] = useState(0);
  const [shouldRun, setShouldRun] = useState(false);
  const stepsRef = useRef<GuidedTourStep[]>([]);
  const moduleIdRef = useRef(moduleId);
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const { t } = useTranslation('dashboard');
  const { user } = useUserContext();
  const { dashboardConfig } = useDashboardContext();

  if (moduleIdRef.current !== moduleId) {
    // Reset steps outside a useEffect because cached data from useQuery is too fast
    stepsRef.current = [];
    moduleIdRef.current = moduleId;
  }

  useEffect(() => {
    if (!dashboardConfig?.show_guided_tour) {
      disableGuidedTour();
    } else {
      enableGuidedTour(true);
    }
    return disableGuidedTour;
  }, [dashboardConfig]);

  useEffect(() => {
    if (state === GuidedTourState.AUTOSTART) {
      setState(GuidedTourState.ACTIVE);
      setShouldRun(true);
    }
  }, [state]);

  const langRef = useRef(i18n.language);
  useEffect(() => {
    if (langRef.current !== i18n.language) {
      langRef.current = i18n.language;
      setIndex(0);
      setState((prev) => {
        if (prev === GuidedTourState.ACTIVE) return GuidedTourState.PENDING;
        if (prev === GuidedTourState.DISABLED) return GuidedTourState.DISABLED;
        return GuidedTourState.INACTIVE;
      });
    }
  }, [i18n.language]);

  const getStep = useCallback(
    (step: StepDataEnhanced) => ({
      id: step.id,
      ...(step.appendToEnd && !step.target
        ? {
            target: 'body',
            placement: 'center' as Placement,
          }
        : {
            target: step.target || `#${GUIDED_TOUR_BUTTON_ID}`,
            placement: step.placement,
          }),
      content: step.text,
      appendToEnd: step.appendToEnd,
      appendToStart: step.appendToStart,
      data: {
        followUpLinks: step.followUpLinks,
        headline: step.headline,
        image: step.image,
        t: t, // Using the translation hook inside the tooltip causes a page refresh
      },
      disableBeacon: true,
    }),
    [t],
  );

  const addSteps = useCallback((steps: Array<StepDataEnhanced | undefined>) => {
    stepsRef.current = steps
      .reduce((acc: GuidedTourStep[], step) => {
        if (step) {
          const stepIndex = acc.findIndex(
            ({ id }) => String(step.id) === String(id),
          );
          if (stepIndex === -1) {
            return [...acc, getStep(step)];
          }
          acc.splice(stepIndex, 1, getStep(step));
        }
        return acc;
      }, stepsRef.current)
      .sort(({ appendToStart: a }, { appendToStart: b }) =>
        // Ensure the compare fn is able to return all of 1, 0, -1, or it will behave different across engines
        (a || false) < (b || false) ? 1 : a === b ? 0 : -1,
      )
      .sort(({ appendToEnd: a }, { appendToEnd: b }) =>
        // Ensure the compare fn is able to return all of 1, 0, -1, or it will behave different across engines
        (a || false) > (b || false) ? 1 : a === b ? 0 : -1,
      );
  }, []);

  const createStep = useCallback(
    (stepId: number | string, stepData: StepUpstream | null) => {
      if (!stepData || !Object.hasOwn(stepData, 'text')) {
        // Early return in case the object is empty or is missing a required field
        return null;
      }
      const iconId = `guided-tour-widget-${stepId}`;
      const targetIcon = <GuidedTourIcon iconId={iconId} />;

      addSteps([{ id: stepId, target: `#${iconId}`, ...stepData }]);

      return targetIcon;
    },
    [addSteps],
  );

  const removeStep = (stepId: number | string) => {
    stepsRef.current = stepsRef.current.filter(
      ({ id }) => String(stepId) !== String(id),
    );
  };

  const disableGuidedTour = () => {
    setIndex(0);
    setState(GuidedTourState.DISABLED);
  };

  const enableGuidedTour = (autoStart = false) => {
    setIndex(0);
    if (autoStart) {
      autoStartTour();
    } else {
      setState((prevState) =>
        // if the tour is enabled while already active the state switches to pending so that the tour
        // can be restarted with startTour or autoStartTour. Useful e.g. for language switches
        prevState === GuidedTourState.ACTIVE
          ? GuidedTourState.PENDING
          : GuidedTourState.INACTIVE,
      );
    }
  };

  const startTour = () => {
    if (!upLg) return;

    if ([GuidedTourState.INACTIVE, GuidedTourState.PENDING].includes(state)) {
      setState(GuidedTourState.ACTIVE);
      setShouldRun(true);
    }
  };

  const startTourAt = (target: string | HTMLElement) => {
    if (![GuidedTourState.INACTIVE, GuidedTourState.PENDING].includes(state))
      return;

    const startIndex = stepsRef.current.findIndex(
      (step) => step.target === target,
    );
    if (startIndex > -1) {
      // To prevent Joyride from not opening the tour at the correct step
      // after the modal has been closed before, we go through the following steps:
      setShouldRun(false); // Stop Joyride first
      setIndex(startIndex); // Set the correct index
      setTimeout(() => {
        // Wait for the state to update before starting the tour
        startTour();
      }, 0);
    }
  };

  const autoStartTour = () => {
    if (state === GuidedTourState.PENDING) {
      startTour();
      return;
    }

    const { guided_tour_completed_modules } = user || {
      guided_tour_completed_modules: [] as number[],
    };
    if (
      guided_tour_completed_modules &&
      (guided_tour_completed_modules?.includes(moduleId) ||
        completedModule.includes(moduleId))
    ) {
      setState(GuidedTourState.INACTIVE);
      return;
    }

    // Joyride will not start if the slides change without setting it to inactive while switching dashboards
    setState(GuidedTourState.AUTOSTART);
  };

  const updateUser = useCallback(
    (moduleId: number) =>
      HttpService.patch(apiPaths.USER.UPDATE_GUIDED_TOUR_COMPLETED_MODULES, {
        guided_tour_completed_modules: [moduleId],
      })
        .then(() => 'OK')
        .catch(() => 'Error'),
    [],
  );

  const terminateTour = () => {
    setIndex(0);
    setShouldRun(false);
    setState(GuidedTourState.INACTIVE);
    setCompletedModule((prevState) =>
      prevState.includes(moduleId) ? prevState : [...prevState, moduleId],
    );
    const { guided_tour_completed_modules } = user || {
      guided_tour_completed_modules: [] as number[],
    };
    if (
      !guided_tour_completed_modules ||
      !guided_tour_completed_modules.includes(moduleId)
    ) {
      updateUser(moduleId).finally();
    }
  };

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, type, index: stepIndex } = data;
      const advanceEvents: string[] = [
        EVENTS.STEP_AFTER,
        EVENTS.TARGET_NOT_FOUND,
      ];
      switch (action) {
        case ACTIONS.NEXT: {
          if (advanceEvents.includes(type)) {
            if (type === EVENTS.STEP_AFTER) {
              const next = stepsRef.current[stepIndex + 1];
              stepAfterHook && next && stepAfterHook(next.target);
            }
            setIndex((prevIndex) => prevIndex + 1);
          } else if (type === EVENTS.TOUR_END) {
            terminateTour();
          }
          break;
        }
        case ACTIONS.PREV: {
          if (advanceEvents.includes(type)) {
            if (type === EVENTS.STEP_AFTER) {
              const next = stepsRef.current[stepIndex - 1];
              stepAfterHook && next && stepAfterHook(next.target);
            }
            setIndex((prevIndex) => prevIndex - 1);
          }
          break;
        }
        case ACTIONS.CLOSE: {
          terminateTour();
          break;
        }
      }
    },
    [setIndex, terminateTour, stepAfterHook],
  );

  return (
    <GuidedTourContext.Provider
      value={{
        addSteps,
        autoStartTour,
        disabled: state === GuidedTourState.DISABLED || !upLg,
        enableGuidedTour,
        disableGuidedTour,
        startTour,
        startTourAt,
        createStep,
        removeStep,
      }}
    >
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        disableOverlay
        disableScrollParentFix // causes issues on the dashboard with collapsed sections
        floaterProps={{
          styles: {
            floater: {
              filter: 'none', // disable any box shadow effect on the floater
            },
            floaterWithAnimation: {
              transition: 'none', // disable floater animations to prevent lag when scrolling
            },
          },
        }}
        run={
          state === GuidedTourState.ACTIVE &&
          !!stepsRef.current.length &&
          shouldRun
        }
        tooltipComponent={GuidedTourTooltip}
        showProgress
        stepIndex={index}
        steps={stepsRef.current}
        styles={{
          options: {
            arrowColor: theme.colors.neutral[800],
          },
        }}
      />
      {children}
    </GuidedTourContext.Provider>
  );
};

const useGuidedTour = () => useContext(GuidedTourContext);

export default useGuidedTour;
